import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Inbox, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { LoadError } from '@/components/campaigns/LoadError'
import { formatDateTime, formatNumber } from '@/components/campaigns/campaignFormat'
import {
  approvedTemplates,
  templateKey,
  templateVariablePositions,
} from '@/components/campaigns/campaignTemplates'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SeccionPlegable } from '@/components/ui/seccion-plegable'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  useAgentNoticeDecisionsQuery,
  useAgentNoticePolicyQuery,
  useCampaignSettingsQuery,
  useUpdateAgentNoticePolicy,
  useWabaTemplatesQuery,
} from '@/queries'
import type { AgentNoticePolicy } from '@/services/agentNoticesService'

const DECISIONS_PAGE_SIZE = 50
/** La plantilla del aviso lleva cinco huecos (design §7); con otros, Meta la rechaza en cada envío. */
const NOTICE_VARIABLES = 5
const ALL = '__all'
const DECISION_RESULTS = ['enviado', 'bajo_umbral', 'duplicado', 'sin_gestor', 'sin_movil', 'error', 'desactivado']

export function AgentNoticeSettingsCard() {
  const { t } = useTranslation()
  return (
    <SeccionPlegable
      numero="09"
      etiqueta={t('agent_notice.label')}
      titulo={t('agent_notice.title')}
      descripcion={t('agent_notice.description')}
    >
      <div className="min-w-0 space-y-8">
        <PolicySection />
        <DecisionsSection />
      </div>
    </SeccionPlegable>
  )
}

function PolicySection() {
  const policy = useAgentNoticePolicyQuery()
  if (policy.isPending) {
    return (
      <div className="space-y-2" data-testid="notice-policy-loading">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    )
  }
  if (policy.isError) return <LoadError error={policy.error} onRetry={() => void policy.refetch()} />
  return <PolicyForm initial={policy.data} />
}

function PolicyForm({ initial }: { initial: AgentNoticePolicy }) {
  const { t } = useTranslation()
  const settings = useCampaignSettingsQuery()
  const templatesQuery = useWabaTemplatesQuery(settings.data?.waba_id)
  const update = useUpdateAgentNoticePolicy()
  const [draft, setDraft] = useState(initial)
  const [errors, setErrors] = useState<{ threshold?: string; template?: string }>({})

  useEffect(() => setDraft(initial), [initial])

  const templates = useMemo(() => approvedTemplates(templatesQuery.data), [templatesQuery.data])
  const selected = templates.find(
    (item) => item.name === draft.plantilla_nombre && item.language === draft.plantilla_idioma,
  )
  const selectedKey = draft.plantilla_nombre ? templateKey(draft.plantilla_nombre, draft.plantilla_idioma) : undefined

  const set = <K extends keyof AgentNoticePolicy>(key: K, value: AgentNoticePolicy[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  const validate = () => {
    const found: typeof errors = {}
    if (!(draft.umbral_confianza >= 0 && draft.umbral_confianza <= 1)) {
      found.threshold = t('agent_notice.error_threshold')
    }
    if (draft.activo && !draft.plantilla_nombre) {
      found.template = t('agent_notice.error_template_required')
    } else if (selected && templateVariablePositions(selected).length !== NOTICE_VARIABLES) {
      found.template = t('agent_notice.error_template_variables', {
        expected: NOTICE_VARIABLES,
        found: templateVariablePositions(selected).length,
      })
    }
    return found
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const found = validate()
    setErrors(found)
    if (Object.keys(found).length > 0) return
    update.mutate(draft, { onSuccess: () => toast.success(t('agent_notice.saved')) })
  }

  const switches: Array<{ key: 'activo' | 'crear_llamada_pendiente' | 'avisar_visita_preagendada'; id: string }> = [
    { key: 'activo', id: 'notice-active' },
    { key: 'crear_llamada_pendiente', id: 'notice-pending-call' },
    { key: 'avisar_visita_preagendada', id: 'notice-booked-visit' },
  ]

  return (
    <form className="space-y-4" onSubmit={submit} noValidate>
      {switches.map(({ key, id }) => (
        <div key={key} className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor={id}>{t(`agent_notice.switch.${key}.label`)}</Label>
            <p className="text-xs text-muted-foreground">{t(`agent_notice.switch.${key}.help`)}</p>
          </div>
          <Switch id={id} aria-label={t(`agent_notice.switch.${key}.label`)} checked={draft[key]} onCheckedChange={(checked) => set(key, checked)} />
        </div>
      ))}

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="notice-threshold">{t('agent_notice.threshold_label')}</Label>
          <Input
            id="notice-threshold"
            aria-label={t('agent_notice.threshold_label')}
            type="number"
            min={0}
            max={1}
            step={0.05}
            value={draft.umbral_confianza}
            aria-invalid={Boolean(errors.threshold)}
            aria-describedby={errors.threshold ? 'notice-threshold-error' : 'notice-threshold-help'}
            onChange={(event) => set('umbral_confianza', Number(event.target.value))}
          />
          <p id="notice-threshold-help" className="text-xs text-muted-foreground">
            {t('agent_notice.threshold_help')}
          </p>
          <FieldError id="notice-threshold-error">{errors.threshold}</FieldError>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="notice-template">{t('agent_notice.template_label')}</Label>
          {settings.isError ? (
            <LoadError error={settings.error} onRetry={() => void settings.refetch()} />
          ) : templatesQuery.isError ? (
            <LoadError error={templatesQuery.error} onRetry={() => void templatesQuery.refetch()} />
          ) : (
            <Select
              value={selectedKey}
              disabled={settings.isPending || templatesQuery.isPending}
              onValueChange={(key) => {
                const template = templates.find((item) => templateKey(item.name, item.language) === key)
                if (template) {
                  setDraft((prev) => ({ ...prev, plantilla_nombre: template.name, plantilla_idioma: template.language }))
                }
              }}
            >
              <SelectTrigger id="notice-template" aria-invalid={Boolean(errors.template)}>
                <SelectValue placeholder={t('agent_notice.template_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {/* Aunque ya no esté aprobada, la guardada se sigue viendo: esconderla la perdería en silencio. */}
                {selectedKey && !selected ? (
                  <SelectItem value={selectedKey}>
                    {draft.plantilla_nombre} ({draft.plantilla_idioma})
                  </SelectItem>
                ) : null}
                {templates.map((item) => (
                  <SelectItem key={templateKey(item.name, item.language)} value={templateKey(item.name, item.language)}>
                    {item.name} ({item.language})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <FieldError>{errors.template}</FieldError>
        </div>
      </div>

      {update.isError ? (
        <Alert variant="destructive">
          <AlertDescription>{update.error.message}</AlertDescription>
        </Alert>
      ) : null}

      <Button type="submit" disabled={update.isPending}>
        {update.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        {t('agent_notice.save')}
      </Button>
    </form>
  )
}

function DecisionsSection() {
  const { t } = useTranslation()
  const { lang = 'es' } = useParams()
  const [page, setPage] = useState(1)
  const [result, setResult] = useState(ALL)
  const decisions = useAgentNoticeDecisionsQuery({ page, resultado: result === ALL ? undefined : result })
  const total = decisions.data?.count ?? 0
  const pageCount = Math.max(1, Math.ceil(total / DECISIONS_PAGE_SIZE))

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <h4 className="text-lg font-semibold">{t('agent_notice.decisions_title')}</h4>
        <div className="space-y-1.5">
          <Label htmlFor="notice-result-filter">{t('agent_notice.result_filter')}</Label>
          <Select
            value={result}
            onValueChange={(value) => {
              setResult(value)
              setPage(1)
            }}
          >
            <SelectTrigger id="notice-result-filter" className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('agent_notice.result_all')}</SelectItem>
              {DECISION_RESULTS.map((item) => (
                <SelectItem key={item} value={item}>
                  {t(`agent_notice.result.${item}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {decisions.isPending ? (
        <div className="space-y-2" data-testid="notice-decisions-loading">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : decisions.isError ? (
        <LoadError error={decisions.error} onRetry={() => void decisions.refetch()} />
      ) : decisions.data.results.length === 0 ? (
        <EmptyState
          icon={<Inbox className="h-6 w-6" />}
          title={result === ALL ? t('agent_notice.decisions_empty') : t('agent_notice.decisions_empty_filtered')}
          description={result === ALL ? t('agent_notice.decisions_empty_help') : undefined}
        />
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('agent_notice.col_date')}</TableHead>
                  <TableHead>{t('agent_notice.col_origin')}</TableHead>
                  <TableHead>{t('agent_notice.col_client')}</TableHead>
                  <TableHead>{t('agent_notice.col_reason')}</TableHead>
                  <TableHead className="text-right">{t('agent_notice.col_confidence')}</TableHead>
                  <TableHead>{t('agent_notice.col_result')}</TableHead>
                  <TableHead>{t('agent_notice.col_summary')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {decisions.data.results.map((decision) => (
                  <TableRow key={decision.id}>
                    <TableCell className="whitespace-nowrap text-xs">{formatDateTime(decision.created_at, lang)}</TableCell>
                    <TableCell>{t(`agent_notice.origin.${decision.origen}`, { defaultValue: decision.origen })}</TableCell>
                    <TableCell className="whitespace-nowrap font-mono text-xs">{decision.telefono_cliente || '—'}</TableCell>
                    <TableCell>
                      {decision.motivo ? t(`agent_notice.reason.${decision.motivo}`, { defaultValue: decision.motivo }) : '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {decision.confianza === null ? '—' : formatNumber(decision.confianza, lang)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={decision.resultado === 'enviado' ? 'secondary' : decision.resultado === 'error' ? 'destructive' : 'outline'} className="whitespace-nowrap font-medium">
                        {t(`agent_notice.result.${decision.resultado}`, { defaultValue: decision.resultado })}
                      </Badge>
                    </TableCell>
                    <TableCell className="min-w-64 text-xs text-muted-foreground">{decision.resumen || decision.error}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>{t('campaigns.common.page_status', { page, pages: pageCount })}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="xs" disabled={page <= 1} onClick={() => setPage((prev) => prev - 1)}>
                {t('campaigns.common.previous')}
              </Button>
              <Button variant="outline" size="xs" disabled={page >= pageCount} onClick={() => setPage((prev) => prev + 1)}>
                {t('campaigns.common.next')}
              </Button>
            </div>
          </div>
        </>
      )}
    </section>
  )
}
